// Dinero: siempre PESOS mexicanos como number, redondeados a centavos con redondearCentavos().
// Regla de Diego: siempre subtotal sin IVA + IVA + total (adenda A3). El IVA es 16 % (8 % en región fronteriza).

export const IVA_GENERAL_PCT = 16;
export const IVA_FRONTERA_PCT = 8;

export function redondearCentavos(n: number): number {
  if (!Number.isFinite(n)) throw new Error(`redondearCentavos: ${n} no es un número finito`);
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function conIva(sinIva: number, ivaPct: number): number {
  return redondearCentavos(sinIva * (1 + ivaPct / 100));
}

export function sinIva(conIvaMonto: number, ivaPct: number): number {
  return redondearCentavos(conIvaMonto / (1 + ivaPct / 100));
}

export function ivaDe(sinIvaMonto: number, ivaPct: number): number {
  return redondearCentavos(sinIvaMonto * (ivaPct / 100));
}

/** Diego, 13-oct (junta: «que el vendedor escriba lo que ve el cliente»): del total de CONTADO CON IVA que teclea el vendedor
 *  al campo que guarda el motor (`AjusteOpcion.precio_lista_sin_iva`: SIEMPRE lista sin IVA, con o sin precio único). Ida y
 *  vuelta exacta: con un contado múltiplo de $100, el motor devuelve ESE contado (lista × 0.85 × 1.16 = contado ± centavos y
 *  el redondeo a $100 lo deja igual). */
export function listaDesdeContado(contadoConIva: number, ivaPct: number, descuentoContadoPct: number): number {
  return redondearCentavos(contadoConIva / (1 + ivaPct / 100) / (1 - descuentoContadoPct / 100));
}

const formatoPesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatoPesosEnteros = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** "$243,440.00" (o "$243,440" con enteros: true). */
export function pesos(n: number, opciones: { enteros?: boolean } = {}): string {
  if (!Number.isFinite(n)) throw new Error(`pesos: ${n} no es un número finito`);
  return (opciones.enteros ? formatoPesosEnteros : formatoPesos).format(n);
}

/** "1,234.5" con separador de miles de México. */
export function numero(n: number, decimales = 0): string {
  if (!Number.isFinite(n)) throw new Error(`numero: ${n} no es un número finito`);
  return new Intl.NumberFormat('es-MX', { minimumFractionDigits: decimales, maximumFractionDigits: decimales }).format(n);
}

/** Ahorro al mes como lo IMPRIME la propuesta: lo de hoy al mes menos lo de con sistema al mes, cada uno ya redondeado a pesos
 *  (plantilla, modeloSolar.mjs: hoyMes − conMes, para que la resta que el cliente haga en la hoja cuadre). La tarjeta de la
 *  opción usa ESTA cuenta para decir la misma cifra que el PDF (verificación adversarial de la etapa 2: la tarjeta decía ahorro
 *  del año ÷ 12, $7,823, y el PDF $7,822). periodos: los del resultado (pago_hoy); pagoConSistema: los de la opción. */
export function ahorroMensualImpreso(periodos: readonly { pago_hoy: number }[], pagoConSistema: readonly number[]): number {
  const hoy = periodos.reduce((s, p) => s + p.pago_hoy, 0);
  const con = pagoConSistema.reduce((s, x) => s + x, 0);
  return Math.round(hoy / 12) - Math.round(con / 12);
}

/** ADENDA z31 (Diego, 26-oct): «si es mensual da todos los datos mensuales, si es bimestral muestra la info bimestral, por
 *  ejemplo te ahorras 3000 al bimestre». El ahorro que dice la tarjeta, en la periodicidad del recibo: bimestral →
 *  round(Σhoy ÷ 6) − round(Σcon ÷ 6) «al bimestre» (la misma cuenta que el PDF, por bimestre); mensual → ahorroMensualImpreso
 *  «al mes · promedio mensual», como siempre. Lo que se compara contra la mensualidad del crédito sigue al mes. */
export function ahorroPorPeriodoImpreso(
  periodos: readonly { pago_hoy: number }[],
  pagoConSistema: readonly number[],
  facturacion: 'mensual' | 'bimestral' | null | undefined,
): { cifra: number; etiqueta: 'al mes' | 'al bimestre'; nota: 'promedio mensual' | null } {
  if (facturacion !== 'bimestral') return { cifra: ahorroMensualImpreso(periodos, pagoConSistema), etiqueta: 'al mes', nota: 'promedio mensual' };
  const hoy = periodos.reduce((s, p) => s + p.pago_hoy, 0);
  const con = pagoConSistema.reduce((s, x) => s + x, 0);
  return { cifra: Math.round(hoy / 6) - Math.round(con / 6), etiqueta: 'al bimestre', nota: null };
}
